"use client";

import * as SwitchPrimitive from "@radix-ui/react-switch";
import * as React from "react";
import { cn } from "@/lib/utils";
import { type CircleActionVariant } from "./circle-action-variants";
import { LiquidSwitch } from "./liquid-switch";

export interface SwitcherV2Props
  extends Omit<
    React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>,
    "className" | "style" | "asChild"
  > {
  variant?: CircleActionVariant;
  label?: string;
  description?: string;
  labelPosition?: "left" | "right";
  dense?: boolean;
}

export const SwitcherV2 = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitcherV2Props
>(
  (
    {
      variant = "indigo",
      label,
      description,
      labelPosition = "right",
      dense = false,
      checked,
      defaultChecked,
      onCheckedChange,
      disabled,
      id,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const switchId = id ?? generatedId;

    const switchControl = (
      <LiquidSwitch
        ref={ref}
        id={switchId}
        variant={variant}
        checked={checked}
        defaultChecked={defaultChecked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        aria-label={!label ? props["aria-label"] : undefined}
        {...props}
      />
    );

    if (!label && !description) {
      return switchControl;
    }

    const hasDescription = Boolean(description);

    return (
      <div
        className={cn(
          "flex",
          hasDescription ? "items-start" : "items-center",
          dense ? "gap-2" : "gap-3",
          labelPosition === "left" && "flex-row-reverse justify-end"
        )}
      >
        <div className={cn("shrink-0", hasDescription && !dense && "mt-0.5")}>
          {switchControl}
        </div>
        <div className={cn("flex min-w-0 flex-col", dense ? "gap-0" : "gap-0.5")}>
          {label && (
            <label
              htmlFor={switchId}
              className={cn(
                "cursor-pointer select-none font-semibold leading-snug text-slate-900 dark:text-zinc-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
                dense ? "text-[13px]" : "text-sm"
              )}
            >
              {label}
            </label>
          )}
          {description && (
            <p
              className={cn(
                "leading-relaxed text-slate-600 dark:text-zinc-300",
                dense ? "text-[11px] leading-snug" : "text-xs"
              )}
            >
              {description}
            </p>
          )}
        </div>
      </div>
    );
  }
);

SwitcherV2.displayName = "SwitcherV2";
